import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  db,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  getDocs,
} from '../firebase';

function sanitizePoints(raw) {
  if (raw === '' || raw === null || raw === undefined) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1_000_000, Math.trunc(n)));
}

function PlansTab({ user }) {
  const { t } = useTranslation();
  const [sprints, setSprints] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [newSprint, setNewSprint] = useState({ title: '', description: '', startDate: '', endDate: '', points: '' });
  const [newItem, setNewItem] = useState({});
  const [descDrafts, setDescDrafts] = useState({});
  const [descSaving, setDescSaving] = useState({});
  const [pointsDrafts, setPointsDrafts] = useState({});
  const [pointsSaving, setPointsSaving] = useState({});
  const uid = user?.uid;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'sprints'), where('userId', '==', uid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      rows.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSprints(rows);
    });
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'planItems'), where('userId', '==', uid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      setAllItems(rows);
    });
  }, [uid]);

  const itemsBySprint = useMemo(() => {
    const items = {};
    for (const it of allItems) {
      const sid = it.sprintId;
      if (!items[sid]) items[sid] = [];
      items[sid].push(it);
    }
    for (const k of Object.keys(items)) {
      items[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return items;
  }, [allItems]);

  const toggleExpand = (sprintId) => {
    setExpanded((e) => ({ ...e, [sprintId]: !e[sprintId] }));
  };

  const handleAddSprint = async () => {
    if (!newSprint.title || !newSprint.startDate || !newSprint.endDate || !uid) return;
    const start = Timestamp.fromDate(new Date(newSprint.startDate));
    const end = Timestamp.fromDate(new Date(newSprint.endDate));
    await addDoc(collection(db, 'sprints'), {
      userId: uid,
      title: newSprint.title.trim(),
      description: newSprint.description.trim() || '',
      startDate: start,
      endDate: end,
      order: sprints.length,
      status: 'planned',
      done: false,
      lateDone: false,
      points: sanitizePoints(newSprint.points),
      awardedPoints: 0,
      createdAt: serverTimestamp(),
    });
    setNewSprint({ title: '', description: '', startDate: '', endDate: '', points: '' });
  };

  const toggleSprintDone = async (sprint) => {
    if (sprint.done) {
      await updateDoc(doc(db, 'sprints', sprint._id), {
        done: false,
        lateDone: false,
        status: 'planned',
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(doc(db, 'sprints', sprint._id), {
        done: true,
        lateDone: false,
        status: 'done',
        updatedAt: serverTimestamp(),
      });
    }
  };

  const markSprintLateDone = async (sprint) => {
    await updateDoc(doc(db, 'sprints', sprint._id), {
      done: true,
      lateDone: true,
      status: 'done',
      updatedAt: serverTimestamp(),
    });
  };

  const saveSprintPoints = async (sprintId) => {
    if (!isAdmin) return;
    const value = sanitizePoints(pointsDrafts[sprintId]);
    setPointsSaving((s) => ({ ...s, [sprintId]: true }));
    try {
      await updateDoc(doc(db, 'sprints', sprintId), {
        points: value,
        updatedAt: serverTimestamp(),
      });
      setPointsDrafts((d) => {
        const next = { ...d };
        delete next[sprintId];
        return next;
      });
    } finally {
      setPointsSaving((s) => ({ ...s, [sprintId]: false }));
    }
  };

  const isSprintDeadlinePassed = (sprint) => {
    if (!sprint?.endDate?.toDate) return false;
    return sprint.endDate.toDate().getTime() < Date.now();
  };

  const saveSprintDescription = async (sprintId) => {
    const value = (descDrafts[sprintId] ?? '').trim();
    setDescSaving((s) => ({ ...s, [sprintId]: true }));
    try {
      await updateDoc(doc(db, 'sprints', sprintId), {
        description: value,
        updatedAt: serverTimestamp(),
      });
      setDescDrafts((d) => {
        const next = { ...d };
        delete next[sprintId];
        return next;
      });
    } finally {
      setDescSaving((s) => ({ ...s, [sprintId]: false }));
    }
  };

  const handleAddItem = async (sprintId) => {
    const title = (newItem[sprintId] || '').trim();
    if (!title || !uid) return;
    const list = itemsBySprint[sprintId] || [];
    await addDoc(collection(db, 'planItems'), {
      sprintId,
      userId: uid,
      title,
      description: '',
      scheduledDate: null,
      order: list.length,
      done: false,
      createdAt: serverTimestamp(),
    });
    setNewItem((prev) => ({ ...prev, [sprintId]: '' }));
  };

  const toggleItemDone = async (item) => {
    await updateDoc(doc(db, 'planItems', item._id), { done: !item.done });
  };

  const deleteSprint = async (id) => {
    if (!window.confirm(t('confirmDeleteSprint'))) return;
    const qs = await getDocs(query(collection(db, 'planItems'), where('sprintId', '==', id)));
    for (const d of qs.docs) await deleteDoc(d.ref);
    await deleteDoc(doc(db, 'sprints', id));
  };

  const deleteItem = async (item) => {
    if (!window.confirm(t('confirmDeleteItem'))) return;
    await deleteDoc(doc(db, 'planItems', item._id));
  };

  const fmt = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString();
  };

  return (
    <div>
      <div className="haydi-pro-hero">
        <h1 className="haydi-pro-hero__title">{t('plansTitle')}</h1>
        <p className="haydi-pro-hero__lead">{t('plansSubtitle')}</p>
      </div>

      <div className="haydi-pro-panel" style={{ marginBottom: '1.25rem' }}>
        <div className="haydi-pro-panel__head">
          <h2>{t('sprintAdd')}</h2>
          <p className="haydi-pro-panel__meta">{t('plansWorkflowHint')}</p>
        </div>
        <div className="haydi-pro-panel__body" style={{ paddingTop: 0 }}>
          <div className="haydi-form-row" style={{ marginBottom: '0.6rem' }}>
            <input
              className="haydi-input haydi-input--inline"
              placeholder={t('sprintName')}
              value={newSprint.title}
              onChange={(e) => setNewSprint((s) => ({ ...s, title: e.target.value }))}
              style={{ flex: 2, minWidth: 140 }}
            />
            <label className="haydi-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem' }}>
              {t('scheduledStart')}
              <input
                type="date"
                className="haydi-input"
                value={newSprint.startDate}
                onChange={(e) => setNewSprint((s) => ({ ...s, startDate: e.target.value }))}
              />
            </label>
            <label className="haydi-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem' }}>
              {t('scheduledEnd')}
              <input
                type="date"
                className="haydi-input"
                value={newSprint.endDate}
                onChange={(e) => setNewSprint((s) => ({ ...s, endDate: e.target.value }))}
              />
            </label>
            <label className="haydi-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem' }}>
              {t('itemPoints')}
              <input
                type="number"
                min={0}
                max={1000000}
                className="haydi-input"
                placeholder="0"
                value={newSprint.points}
                onChange={(e) => setNewSprint((s) => ({ ...s, points: e.target.value }))}
              />
            </label>
            <button type="button" className="haydi-btn haydi-btn--primary" onClick={handleAddSprint}>
              {t('add')}
            </button>
          </div>
          <p className="haydi-muted haydi-hint-xs" style={{ margin: '0 0 0.5rem' }}>{t('itemPointsHint')}</p>
          <label className="haydi-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: 4 }}>
            {t('sprintDescription')}
          </label>
          <textarea
            className="haydi-input"
            rows={3}
            placeholder={t('sprintDescriptionPlaceholder')}
            value={newSprint.description}
            onChange={(e) => setNewSprint((s) => ({ ...s, description: e.target.value }))}
          />
        </div>
      </div>

      {sprints.length === 0 && <p className="haydi-muted">{t('noSprints')}</p>}

      <ul className="haydi-plans-sprint-grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {sprints.map((sp) => {
          const items = itemsBySprint[sp._id] || [];
          const doneCount = items.filter((it) => it.done).length;
          const total = items.length;
          const pct = total ? Math.round((doneCount / total) * 100) : 0;
          const pts = Number(sp.points || 0);
          const awarded = Number(sp.awardedPoints || 0);
          const overdueUndone = !sp.done && isSprintDeadlinePassed(sp);
          const statusKey = sp.done ? 'done' : (sp.status || 'planned');
          return (
          <li key={sp._id} className="haydi-sprint">
            <div
              role="button"
              tabIndex={0}
              className={`haydi-sprint__head${expanded[sp._id] ? ' haydi-sprint__head--open' : ''}`}
              onClick={() => toggleExpand(sp._id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(sp._id);
                }
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem' }}>
                  <strong>{sp.title}</strong>
                  <span
                    className={`haydi-badge${sp.status === 'active' ? ' haydi-badge--active' : ''}${sp.done ? ' haydi-badge--done' : ''}`}
                  >
                    {t(`sprintStatus_${statusKey}`)}
                  </span>
                  {pts > 0 && (
                    <span className="haydi-chip">
                      {sp.done
                        ? t('itemPointsAwardedChip', { n: awarded })
                        : t('itemPointsChip', { n: pts })}
                    </span>
                  )}
                  {sp.done && sp.lateDone && (
                    <span className="haydi-chip haydi-chip--med">{t('itemLateDoneChip')}</span>
                  )}
                  <span className="haydi-sprint__progress">
                    {t('plansProgressLabel', { done: doneCount, total })}
                  </span>
                </div>
                <span className="haydi-muted" style={{ fontSize: '0.8125rem', display: 'block', marginTop: '0.25rem' }}>
                  {fmt(sp.startDate)} – {fmt(sp.endDate)}
                </span>
                <div className="haydi-sprint__progress-bar" style={{ maxWidth: 200, marginTop: '0.45rem' }}>
                  <div className="haydi-sprint__progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={`haydi-btn haydi-btn--sm ${sp.done ? 'haydi-btn--ghost' : 'haydi-btn--primary'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSprintDone(sp);
                  }}
                >
                  {sp.done ? t('markNotDone') : t('markDone')}
                </button>
                {overdueUndone && (
                  <button
                    type="button"
                    className="haydi-btn haydi-btn--secondary haydi-btn--sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      markSprintLateDone(sp);
                    }}
                    title={t('markLateDoneHint')}
                  >
                    {t('markLateDone')}
                  </button>
                )}
                <button
                  type="button"
                  className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSprint(sp._id);
                  }}
                >
                  {t('delete')}
                </button>
              </div>
            </div>

            {expanded[sp._id] && (
              <div className="haydi-sprint__body">
                {isAdmin && (() => {
                  const draft = pointsDrafts[sp._id];
                  const value = draft !== undefined ? draft : String(pts);
                  const dirty = draft !== undefined && sanitizePoints(draft) !== pts;
                  return (
                    <div style={{ marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <label
                        className="haydi-muted"
                        style={{ fontSize: '0.75rem', fontWeight: 600, margin: 0 }}
                      >
                        {t('itemPoints')}
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={1000000}
                        className="haydi-input"
                        style={{ width: 120 }}
                        value={value}
                        onChange={(e) =>
                          setPointsDrafts((d) => ({ ...d, [sp._id]: e.target.value }))
                        }
                      />
                      {dirty && (
                        <>
                          <button
                            type="button"
                            className="haydi-btn haydi-btn--primary haydi-btn--sm"
                            onClick={() => saveSprintPoints(sp._id)}
                            disabled={!!pointsSaving[sp._id]}
                          >
                            {pointsSaving[sp._id] ? '…' : t('save')}
                          </button>
                          <button
                            type="button"
                            className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                            onClick={() =>
                              setPointsDrafts((d) => {
                                const next = { ...d };
                                delete next[sp._id];
                                return next;
                              })
                            }
                          >
                            {t('cancel')}
                          </button>
                        </>
                      )}
                      <span className="haydi-muted haydi-hint-xs" style={{ marginLeft: 'auto' }}>{t('itemPointsHint')}</span>
                    </div>
                  );
                })()}
                {(() => {
                  const saved = typeof sp.description === 'string' ? sp.description : '';
                  const draft = descDrafts[sp._id];
                  const value = draft !== undefined ? draft : saved;
                  const dirty = value.trim() !== saved.trim();
                  return (
                    <div style={{ marginBottom: '0.9rem' }}>
                      <label
                        className="haydi-muted"
                        style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}
                      >
                        {t('sprintDescription')}
                      </label>
                      <textarea
                        className="haydi-input"
                        rows={3}
                        placeholder={t('sprintDescriptionPlaceholder')}
                        value={value}
                        onChange={(e) =>
                          setDescDrafts((d) => ({ ...d, [sp._id]: e.target.value }))
                        }
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                        {dirty && (
                          <button
                            type="button"
                            className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                            onClick={() =>
                              setDescDrafts((d) => {
                                const next = { ...d };
                                delete next[sp._id];
                                return next;
                              })
                            }
                          >
                            {t('cancel')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="haydi-btn haydi-btn--primary haydi-btn--sm"
                          onClick={() => saveSprintDescription(sp._id)}
                          disabled={!dirty || !!descSaving[sp._id]}
                        >
                          {descSaving[sp._id] ? '…' : t('save')}
                        </button>
                      </div>
                    </div>
                  );
                })()}
                <div className="haydi-form-row">
                  <input
                    className="haydi-input haydi-input--inline"
                    placeholder={t('planItemPlaceholder')}
                    value={newItem[sp._id] || ''}
                    onChange={(e) => setNewItem((prev) => ({ ...prev, [sp._id]: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="haydi-btn haydi-btn--primary"
                    onClick={() => handleAddItem(sp._id)}
                  >
                    {t('add')}
                  </button>
                </div>
                <ul className="haydi-stack-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(itemsBySprint[sp._id] || []).map((it) => (
                    <li
                      key={it._id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid var(--haydi-border)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!it.done}
                          onChange={() => toggleItemDone(it)}
                        />
                        <span
                          style={{
                            textDecoration: it.done ? 'line-through' : 'none',
                            color: it.done ? 'var(--haydi-text-muted)' : 'var(--haydi-text)',
                          }}
                        >
                          {it.title}
                        </span>
                      </label>
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                        onClick={() => deleteItem(it)}
                      >
                        {t('delete')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
          );
        })}
      </ul>
    </div>
  );
}

export default PlansTab;
